import { FOOT_HALF_WIDTH, FOOT_HEIGHT, SIXTEENTHS } from "../movement/step.ts";
import { VISION_CAP_TILES } from "../vision/vision.ts";
import { aimX, aimY } from "./aim.ts";

/**
 * The shapes an ability reaches with, tested against feet boxes (ADR-073 decision 7, Bible 13.4). Every shape starts at
 * the caster's origin, the centre of its feet box, so reach is the same up and down. A target's feet box, from `x - 3`
 * to `x + 3` and `y - 4` to `y` with its edges, is first clipped to the caster's reach rectangle (the vision clause:
 * the default vision cap about the caster's feet, narrowed by its current vision), then tested against the shape. The
 * geometry is floating point and runs only on the map server, which alone decides a hit; a caller prepares a shape once
 * per act into a record it keeps, so testing a target allocates nothing.
 */

/**
 * Half the reach rectangle's width and height in sixteenths: the default vision cap of 27 by 15 tiles, centred on the
 * caster's feet point.
 */
export const REACH_HALF_WIDTH = (VISION_CAP_TILES.width * SIXTEENTHS) / 2;
export const REACH_HALF_HEIGHT = (VISION_CAP_TILES.height * SIXTEENTHS) / 2;

/**
 * How far from the caster's feet point up its origin sits: half the feet box's height.
 */
export const ORIGIN_RISE = FOOT_HEIGHT / 2;

/**
 * Slack for the floating-point tests, far below a sixteenth, so a box touching a cone's edge counts as inside it.
 */
const EPSILON = 1e-7;
const FULL_TURN = 360;
const HALF_TURN = 180;
const DEGREES_TO_RADIANS = Math.PI / HALF_TURN;
const MAX_POLYGON = 8;

/**
 * A cone prepared for one act: its origin, its squared radius, the half-planes of the one or two wedges it is made of
 * (none for a full turn; two for a cone wider than a half turn, each half its angle), the reach rectangle, and scratch
 * polygons for the clip.
 */
export interface Cone {
  originX: number;
  originY: number;
  radiusSquared: number;
  wedges: number;
  readonly normals: Float64Array;
  reachLeft: number;
  reachTop: number;
  reachRight: number;
  reachBottom: number;
  readonly scratchA: Float64Array;
  readonly scratchB: Float64Array;
}

/**
 * A cone record to prepare with `aimCone` and reuse for every act.
 *
 * @returns The record.
 */
export function newCone(): Cone {
  return { originX: 0, originY: 0, radiusSquared: 0, wedges: 0, normals: new Float64Array(8), reachLeft: 0, reachTop: 0, reachRight: 0, reachBottom: 0, scratchA: new Float64Array(2 * MAX_POLYGON), scratchB: new Float64Array(2 * MAX_POLYGON) };
}

/**
 * Prepares a cone from a caster along an aim: every point within the radius of the origin whose direction lies within
 * half the angle of the aim, edges included; its reach rectangle is the vision cap about the caster's feet.
 *
 * @param cone The record to fill.
 * @param casterX The caster's feet x in sixteenths.
 * @param casterY The caster's feet y in sixteenths.
 * @param aim The aim in 65,536ths of a turn.
 * @param radius The radius in sixteenths.
 * @param degrees The whole angle, 1 to 360.
 */
export function aimCone(cone: Cone, casterX: number, casterY: number, aim: number, radius: number, degrees: number): void {
  cone.originX = casterX;
  cone.originY = casterY - ORIGIN_RISE;
  cone.radiusSquared = radius * radius;
  cone.reachLeft = casterX - REACH_HALF_WIDTH;
  cone.reachRight = casterX + REACH_HALF_WIDTH;
  cone.reachTop = casterY - REACH_HALF_HEIGHT;
  cone.reachBottom = casterY + REACH_HALF_HEIGHT;
  if (degrees >= FULL_TURN) {
    cone.wedges = 0;
    return;
  }
  const centre = Math.atan2(aimY(aim), aimX(aim));
  if (degrees <= HALF_TURN) {
    cone.wedges = 1;
    setWedge(cone.normals, 0, centre, (degrees / 2) * DEGREES_TO_RADIANS);
    return;
  }
  cone.wedges = 2;
  const quarter = (degrees / 4) * DEGREES_TO_RADIANS;
  setWedge(cone.normals, 0, centre - quarter, quarter);
  setWedge(cone.normals, 4, centre + quarter, quarter);
}

/**
 * Narrows a prepared shape's reach rectangle to a caster's current vision, so a widened vision never lengthens a reach
 * and a narrowed one shortens it (the vision clause, ADR-061 decision 6).
 *
 * @param cone The prepared cone.
 * @param left The vision's left edge in sixteenths.
 * @param top Its top edge.
 * @param right Its right edge.
 * @param bottom Its bottom edge.
 */
export function limitReach(cone: Cone, left: number, top: number, right: number, bottom: number): void {
  cone.reachLeft = Math.max(cone.reachLeft, left);
  cone.reachTop = Math.max(cone.reachTop, top);
  cone.reachRight = Math.min(cone.reachRight, right);
  cone.reachBottom = Math.min(cone.reachBottom, bottom);
}

/**
 * Whether a prepared cone shares a point with a target's feet box clipped to the reach rectangle: the box is clipped by
 * each wedge's two half-planes, and the cone hits when a clipped polygon is not empty and its nearest point to the
 * origin lies within the radius, compared in squared sixteenths.
 *
 * @param cone The prepared cone.
 * @param targetX The target's feet x in sixteenths.
 * @param targetY The target's feet y in sixteenths.
 * @returns True on a hit.
 */
export function coneHits(cone: Cone, targetX: number, targetY: number): boolean {
  const left = Math.max(targetX - FOOT_HALF_WIDTH, cone.reachLeft) - cone.originX;
  const right = Math.min(targetX + FOOT_HALF_WIDTH, cone.reachRight) - cone.originX;
  const top = Math.max(targetY - FOOT_HEIGHT, cone.reachTop) - cone.originY;
  const bottom = Math.min(targetY, cone.reachBottom) - cone.originY;
  if (left > right || top > bottom) {
    return false;
  }
  const dx = left > 0 ? left : right < 0 ? right : 0;
  const dy = top > 0 ? top : bottom < 0 ? bottom : 0;
  if (dx * dx + dy * dy > cone.radiusSquared) {
    return false;
  }
  if (cone.wedges === 0) {
    return true;
  }
  for (let wedge = 0; wedge < cone.wedges; wedge += 1) {
    if (wedgeHits(cone, wedge * 4, left, top, right, bottom)) {
      return true;
    }
  }
  return false;
}

/**
 * The squared distance from an origin to the nearest point of a target's feet box, the order a shape takes its
 * targets in, nearest first.
 *
 * @param originX The origin's x in sixteenths.
 * @param originY The origin's y in sixteenths.
 * @param targetX The target's feet x.
 * @param targetY The target's feet y.
 * @returns The squared distance, 0 when the origin lies in the box.
 */
export function boxDistanceSquared(originX: number, originY: number, targetX: number, targetY: number): number {
  const dx = Math.max(targetX - FOOT_HALF_WIDTH - originX, 0, originX - targetX - FOOT_HALF_WIDTH);
  const dy = Math.max(targetY - FOOT_HEIGHT - originY, 0, originY - targetY);
  return dx * dx + dy * dy;
}

/**
 * Writes the two inward normals of a wedge at most a half turn wide: a point lies in it when both dot products are at
 * least 0.
 *
 * @param normals Where the four numbers go.
 * @param at The first of the four.
 * @param centre The wedge's centre angle in radians.
 * @param half Half its angle in radians.
 */
function setWedge(normals: Float64Array, at: number, centre: number, half: number): void {
  const firstX = Math.cos(centre - half);
  const firstY = Math.sin(centre - half);
  const secondX = Math.cos(centre + half);
  const secondY = Math.sin(centre + half);
  normals[at] = -firstY;
  normals[at + 1] = firstX;
  normals[at + 2] = secondY;
  normals[at + 3] = -secondX;
}

/**
 * Whether one wedge of a cone reaches a box given relative to the origin.
 *
 * @param cone The prepared cone.
 * @param at The wedge's first normal in `normals`.
 * @param left The box's left edge relative to the origin.
 * @param top Its top edge.
 * @param right Its right edge.
 * @param bottom Its bottom edge.
 * @returns True when the clipped box is not empty and its nearest point lies within the radius.
 */
function wedgeHits(cone: Cone, at: number, left: number, top: number, right: number, bottom: number): boolean {
  const a = cone.scratchA;
  a[0] = left;
  a[1] = top;
  a[2] = right;
  a[3] = top;
  a[4] = right;
  a[5] = bottom;
  a[6] = left;
  a[7] = bottom;
  const normals = cone.normals;
  const first = clip(a, 4, normals[at] as number, normals[at + 1] as number, cone.scratchB);
  if (first === 0) {
    return false;
  }
  const second = clip(cone.scratchB, first, normals[at + 2] as number, normals[at + 3] as number, a);
  if (second === 0) {
    return false;
  }
  return nearestSquared(a, second) <= cone.radiusSquared + EPSILON;
}

/**
 * Clips a convex polygon by the half-plane `n . p >= 0` (Sutherland-Hodgman), points on the line kept.
 *
 * @param input The polygon's points as x, y pairs.
 * @param count Its points.
 * @param nx The normal's x.
 * @param ny The normal's y.
 * @param output Where the clipped points go.
 * @returns The clipped polygon's points, 0 when nothing is left.
 */
function clip(input: Float64Array, count: number, nx: number, ny: number, output: Float64Array): number {
  let written = 0;
  for (let i = 0; i < count; i += 1) {
    const px = input[2 * i] as number;
    const py = input[2 * i + 1] as number;
    const j = (i + 1) % count;
    const qx = input[2 * j] as number;
    const qy = input[2 * j + 1] as number;
    const dp = nx * px + ny * py;
    const dq = nx * qx + ny * qy;
    const pIn = dp >= -EPSILON;
    const qIn = dq >= -EPSILON;
    if (pIn) {
      output[2 * written] = px;
      output[2 * written + 1] = py;
      written += 1;
    }
    if (pIn !== qIn) {
      const t = dp / (dp - dq);
      output[2 * written] = px + (qx - px) * t;
      output[2 * written + 1] = py + (qy - py) * t;
      written += 1;
    }
  }
  return written;
}

/**
 * The squared distance from the origin to the nearest point of a convex polygon: 0 when the origin lies inside it,
 * else the least over its edges.
 *
 * @param points The polygon's points as x, y pairs, relative to the origin.
 * @param count Its points, at least 1.
 * @returns The squared distance.
 */
function nearestSquared(points: Float64Array, count: number): number {
  let best = Number.POSITIVE_INFINITY;
  let positive = false;
  let negative = false;
  for (let i = 0; i < count; i += 1) {
    const px = points[2 * i] as number;
    const py = points[2 * i + 1] as number;
    const j = (i + 1) % count;
    const qx = points[2 * j] as number;
    const qy = points[2 * j + 1] as number;
    const cross = px * qy - py * qx;
    if (cross > EPSILON) positive = true;
    if (cross < -EPSILON) negative = true;
    const ex = qx - px;
    const ey = qy - py;
    const length = ex * ex + ey * ey;
    const t = length === 0 ? 0 : Math.min(1, Math.max(0, -(px * ex + py * ey) / length));
    const cx = px + ex * t;
    const cy = py + ey * t;
    best = Math.min(best, cx * cx + cy * cy);
  }
  return count >= 3 && positive !== negative ? 0 : best;
}
